import { PosterProduct } from "@/domain/entities/CreateBookingV3";

export function resolveDefaultProductName(
  configOrder: string[],
  upsellingData: PosterProduct[],
  currentProductName: string
): string {
  const sorted = configOrder
    .map((productType) =>
      upsellingData.find(
        (product) =>
          product.productType.toLowerCase() === productType.toLowerCase()
      )
    )
    .filter((product): product is PosterProduct => !!product);

  const haveValue = sorted.some(
    (product) => product.productType.toUpperCase() === currentProductName
  );

  if (haveValue) return currentProductName;
  return sorted[0]?.productType.toUpperCase() ?? "";
}

// Extension of resolveDefaultProductName for Retail booking.
// orderedProducts is already sorted by applyRecommendationOrder (string[] of productType).
// Tier evaluation (rebooking-aware, PRD §3.3):
//   1. hasPickedManually && currentSelection still available → preserve
//   2. hasRecommended → pick topmost (first recommended after reorder)
//   3. !hasRecommended && carryOverProductName in orderedProducts → pick carry-over
//   4. Else → "" (no auto-select when nothing is recommended/carried/picked)
// carryOverProductName and hasRecommended are optional for backward-compat with non-rebooking callers.
export function resolveSelection(
  orderedProducts: string[],
  currentSelection: string,
  hasPickedManually: boolean,
  carryOverProductName?: string | null,
  hasRecommended?: boolean,
  availableTypes?: string[]
): string {
  if (!orderedProducts.length) return "";
  const topmost = orderedProducts[0].toUpperCase();
  const isAvailable = (type: string): boolean =>
    (availableTypes ?? orderedProducts).some(
      (p) => p.toUpperCase() === type.toUpperCase()
    );

  // Tier 1: manual pick preserved — only if product still has upselling data
  if (hasPickedManually) {
    if (!currentSelection) return "";
    return isAvailable(currentSelection)
      ? currentSelection.toUpperCase()
      : availableTypes !== undefined && availableTypes.length
      ? availableTypes?.[0].toUpperCase() ?? ""
      : "";
  }

  // Tier 2: recommended product at front — topmost is the recommended one
  if (hasRecommended) return topmost;

  // Tier 3: no recommendation, carry-over available in current rates
  if (carryOverProductName) {
    const carryOverUpper = carryOverProductName.toUpperCase();
    if (isAvailable(carryOverUpper)) return carryOverUpper;
  }

  // Tier 4: no recommendation and no carry-over → don't auto-select
  if (availableTypes !== undefined && availableTypes.length) {
    return availableTypes?.[0].toUpperCase() ?? "";
  } else {
    return "";
  }
}
